import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import ConfirmDialog from "../components/ConfirmDialog";
import ProductCategoryModal from "../components/ProductCategoryModal";
import Toast from "../components/Toast";
import { apiErrorMessage } from "../services/api";
import { deleteProductCategory, fetchProductCategories } from "../services/catalogService";

export default function ProductCategories() {
  const { t, i18n } = useTranslation();

  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState("");
  const [toast, setToast] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setCategories(await fetchProductCategories());
    } catch (err) {
      setError(apiErrorMessage(err, t("productCategories.loadFailed")));
      setCategories([]);
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load, i18n.language]);

  /** Roots first, each followed by its own children — the tree, flattened. */
  const ordered = useMemo(() => {
    const roots = categories.filter((category) => !category.parent_id);
    return roots.flatMap((root) => [
      { ...root, depth: 0 },
      ...categories
        .filter((child) => child.parent_id === root.id)
        .map((child) => ({ ...child, depth: 1 })),
    ]);
  }, [categories]);

  const confirmDelete = async () => {
    setDeleteError("");
    setDeleteBusy(true);
    try {
      await deleteProductCategory(deleting.id);
      setToast({ type: "success", message: t("productCategories.deleted", { name: deleting.name }) });
      setDeleting(null);
      load();
    } catch (err) {
      setDeleteError(apiErrorMessage(err, t("productCategories.deleteFailed")));
    } finally {
      setDeleteBusy(false);
    }
  };

  return (
    <div>
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold">{t("productCategories.title")}</h1>
          <p className="text-sm text-ink-2">{t("productCategories.subtitle")}</p>
        </div>
        <button
          type="button"
          onClick={() => {
            setEditing(null);
            setModalOpen(true);
          }}
          className="btn-primary"
        >
          {t("productCategories.new")}
        </button>
      </div>

      {error && (
        <div className="mb-4 rounded-lg border border-danger-line bg-danger-wash px-3 py-2 text-sm text-danger-text">
          {error}
        </div>
      )}

      <div className="card overflow-hidden">
        {loading ? (
          <p className="px-4 py-10 text-center text-sm text-ink-2">{t("common.loading")}</p>
        ) : ordered.length === 0 ? (
          <p className="px-4 py-12 text-center text-sm text-ink-2">
            {t("productCategories.empty")}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-start text-sm">
              <thead className="border-b border-line bg-raised text-xs uppercase tracking-wide text-ink-2">
                <tr>
                  <th className="px-4 py-3">{t("productCategories.table.name")}</th>
                  <th className="px-4 py-3">{t("productCategories.table.products")}</th>
                  <th className="px-4 py-3">{t("products.table.status")}</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody>
                {ordered.map((category) => (
                  <tr key={category.id} className="border-b border-line last:border-0">
                    <td className="px-4 py-3">
                      <span className={category.depth ? "ms-6 text-ink-2" : "font-medium"}>
                        {category.depth ? "└ " : ""}
                        {category.name}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-ink-2">{category.products_count ?? 0}</td>
                    <td className="px-4 py-3">
                      {category.is_active ? (
                        <span className="rounded-full bg-accent-wash px-3 py-1 text-xs font-medium text-accent-text">
                          {t("products.active")}
                        </span>
                      ) : (
                        <span className="rounded-full bg-danger-wash px-3 py-1 text-xs font-medium text-danger-text">
                          {t("products.inactive")}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-end">
                      <div className="flex justify-end gap-3">
                        <button
                          type="button"
                          onClick={() => {
                            setEditing(category);
                            setModalOpen(true);
                          }}
                          className="text-sm font-medium text-accent-text hover:underline"
                        >
                          {t("common.edit")}
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setDeleteError("");
                            setDeleting(category);
                          }}
                          className="text-sm font-medium text-danger-text hover:underline"
                        >
                          {t("common.delete")}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <ProductCategoryModal
        category={editing}
        categories={categories}
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSaved={(saved, wasEditing) => {
          setToast({
            type: "success",
            message: wasEditing
              ? t("productCategories.updated", { name: saved.name })
              : t("productCategories.created", { name: saved.name }),
          });
          load();
        }}
      />

      <ConfirmDialog
        open={Boolean(deleting)}
        title={t("productCategories.deleteTitle")}
        message={t("productCategories.deleteMessage", { name: deleting?.name ?? "" })}
        busy={deleteBusy}
        error={deleteError}
        onConfirm={confirmDelete}
        onClose={() => setDeleting(null)}
      />

      <Toast toast={toast} onClose={() => setToast(null)} />
    </div>
  );
}
